import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { loginPageGuard } from './core/auth/login-page.guard';
import { planSessionCanDeactivateGuard } from './features/plans/plan-session-can-deactivate';
import {
  planProgramContentGuard,
  planProgramTabsGuard,
} from './features/plans/plan-program-content.guard';
export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  {
    path: 'login',
    canMatch: [loginPageGuard],
    loadComponent: () =>
      import('./features/auth/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./features/auth/unauthorized.page').then(
        (m) => m.UnauthorizedPage,
      ),
  },
  {
    path: 'home',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/home/home.page').then((m) => m.HomePage),
  },
  {
    path: 'plans/new',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/plans/plan-editor.page').then((m) => m.PlanEditorPage),
  },
  {
    path: 'plans/:planId/edit',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/plans/plan-editor.page').then((m) => m.PlanEditorPage),
  },
  {
    path: 'plans/:planId',
    canMatch: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/plans/plan-tabs.page').then((m) => m.PlanTabsPage),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./features/plans/plan-overview.tab').then(
                (m) => m.PlanOverviewTab,
              ),
          },
          {
            path: 'blocks',
            pathMatch: 'full',
            redirectTo: 'programs',
          },
          {
            path: 'programs',
            loadComponent: () =>
              import('./features/plans/plan-programs.tab').then(
                (m) => m.PlanProgramsTab,
              ),
          },
          {
            path: 'workouts',
            loadComponent: () =>
              import('./features/plans/plan-active-program.redirect').then(
                (m) => m.PlanActiveProgramRedirect,
              ),
            data: { segment: 'workouts' },
          },
          {
            path: 'schedule',
            pathMatch: 'full',
            redirectTo: 'split',
          },
          {
            path: 'split',
            loadComponent: () =>
              import('./features/plans/plan-active-program.redirect').then(
                (m) => m.PlanActiveProgramRedirect,
              ),
            data: { segment: 'split' },
          },
        ],
      },
      {
        path: 'blocks/:programId',
        children: [
          {
            path: '**',
            redirectTo: (snap) => {
              const programId = snap.params['programId'] ?? '';
              const rest = snap.url.map((s) => s.path).join('/');
              return rest
                ? `programs/${programId}/${rest}`
                : `programs/${programId}`;
            },
          },
        ],
      },
      {
        path: 'programs/:programId/build',
        loadComponent: () =>
          import('./features/plans/plan-program-build.page').then(
            (m) => m.PlanProgramBuildPage,
          ),
      },
      {
        path: 'programs/:programId/generation-failed',
        loadComponent: () =>
          import('./features/plans/plan-program-generation-failed.page').then(
            (m) => m.PlanProgramGenerationFailedPage,
          ),
      },
      {
        path: 'programs/:programId',
        canActivate: [planProgramTabsGuard],
        loadComponent: () =>
          import('./features/plans/plan-program-tabs.page').then(
            (m) => m.PlanProgramTabsPage,
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./features/plans/plan-program-overview.tab').then(
                (m) => m.PlanProgramOverviewTab,
              ),
          },
          {
            path: 'workouts/new',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-add-workout.page').then(
                (m) => m.PlanAddWorkoutPage,
              ),
          },
          {
            path: 'workouts/:workoutId/edit',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-add-workout.page').then(
                (m) => m.PlanAddWorkoutPage,
              ),
          },
          {
            path: 'workouts',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-workouts.tab').then(
                (m) => m.PlanWorkoutsTab,
              ),
          },
          {
            path: 'schedule',
            pathMatch: 'full',
            redirectTo: 'split',
          },
          {
            path: 'split',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-schedule.tab').then(
                (m) => m.PlanScheduleTab,
              ),
          },
          {
            path: 'sessions/start',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-session-start.page').then(
                (m) => m.PlanSessionStartPage,
              ),
          },
          {
            path: 'sessions/:sessionId/finish',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-session-finish.page').then(
                (m) => m.PlanSessionFinishPage,
              ),
          },
          {
            path: 'sessions/:sessionId',
            canActivate: [planProgramContentGuard],
            canDeactivate: [planSessionCanDeactivateGuard],
            loadComponent: () =>
              import('./features/plans/plan-session.page').then(
                (m) => m.PlanSessionPage,
              ),
          },
          {
            path: 'sessions',
            canActivate: [planProgramContentGuard],
            loadComponent: () =>
              import('./features/plans/plan-sessions.tab').then(
                (m) => m.PlanSessionsTab,
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'plans',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/plans/plans.page').then((m) => m.PlansPage),
  },
  {
    path: 'gyms/add',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/add-gym.page').then((m) => m.AddGymPage),
  },
  {
    path: 'gyms/:gymId/edit',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/edit-gym.page').then((m) => m.EditGymPage),
  },
  {
    path: 'gyms/:gymId/equipment/add/camera/results',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gym-equipment-identify-results.page').then(
        (m) => m.GymEquipmentIdentifyResultsPage,
      ),
  },
  {
    path: 'gyms/:gymId/equipment/add/camera/identity',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gym-equipment-identify-identity.page').then(
        (m) => m.GymEquipmentIdentifyIdentityPage,
      ),
  },
  {
    path: 'gyms/:gymId/equipment/add/camera',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gym-equipment-identify.page').then(
        (m) => m.GymEquipmentIdentifyPage,
      ),
  },
  {
    path: 'gyms/:gymId/equipment/add',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/add-gym-equipment.page').then(
        (m) => m.AddGymEquipmentPage,
      ),
  },
  {
    path: 'gyms/:gymId/equipment/:equipmentId',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gym-equipment-detail.page').then(
        (m) => m.GymEquipmentDetailPage,
      ),
  },
  {
    path: 'gyms/:gymId/equipment',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gym-equipment.page').then(
        (m) => m.GymEquipmentPage,
      ),
  },
  {
    path: 'gyms/:gymId',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gym-detail.page').then((m) => m.GymDetailPage),
  },
  {
    path: 'gyms',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/gyms/gyms.page').then((m) => m.GymsPage),
  },
  {
    path: 'coaches/new',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/coaches/coach-editor.page').then(
        (m) => m.CoachEditorPage,
      ),
  },
  {
    path: 'coaches/:coachId/edit',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/coaches/coach-editor.page').then(
        (m) => m.CoachEditorPage,
      ),
  },
  {
    path: 'coaches',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/coaches/coaches.page').then((m) => m.CoachesPage),
  },
  {
    path: 'settings',
    canMatch: [authGuard],
    loadComponent: () =>
      import('./features/settings/settings.page').then((m) => m.SettingsPage),
  },
  { path: '**', redirectTo: 'home' },
];
